import React, { useState, useEffect } from 'react';
import { setPageMeta } from '../utils/setPageMeta';
import { ArrowLeft, Calendar, User, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';

const BlogPost1 = () => {
  useEffect(() => {
    setPageMeta(
      'Understanding LLC Formation: A Complete Guide - Legal Flick Blog',
      'Learn everything about forming an LLC including benefits, tax advantages, asset protection, and step-by-step formation process. Expert guidance from Legal Flick.'
    );
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Blog Post Content */}
      <article className="flex-1 py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            className="mb-8"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>

          {/* Article Header */}
          <header className="mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Understanding LLC Formation: A Complete Guide
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8">
              <div className="flex items-center">
                <User className="w-5 h-5 mr-2" />
                <span>Legal Team</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                <span>8 min read</span>
              </div>
            </div>

            <img
              src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop"
              alt="LLC Formation Guide"
              className="w-full h-[400px] object-cover rounded-lg shadow-lg"
            />
          </header>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What is an LLC?</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              A Limited Liability Company (LLC) is a flexible business structure that combines the liability protection of a corporation with the tax benefits and operational simplicity of a partnership. It's become the most popular choice for small business owners across the United States.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why Form an LLC?</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Forming an LLC offers several key advantages for business owners:
            </p>
            <ul className="list-disc pl-6 mb-8 space-y-3 text-gray-700">
              <li><strong>Personal Asset Protection:</strong> Your personal assets are protected from business debts and legal liabilities</li>
              <li><strong>Tax Flexibility:</strong> Choose how you want to be taxed - as a sole proprietorship, partnership, S-corp, or C-corp</li>
              <li><strong>Credibility:</strong> Adding "LLC" to your business name builds trust with customers and vendors</li>
              <li><strong>Simple Management:</strong> Less paperwork and fewer compliance requirements than corporations</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Steps to Form an LLC</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              The LLC formation process involves several important steps:
            </p>
            <ol className="list-decimal pl-6 mb-8 space-y-3 text-gray-700">
              <li><strong>Choose a business name:</strong> Your name must be unique and comply with state naming requirements</li>
              <li><strong>Appoint a registered agent:</strong> This person or service receives legal documents on behalf of your LLC</li>
              <li><strong>File Articles of Organization:</strong> Submit this document to your state's business filing office</li>
              <li><strong>Create an Operating Agreement:</strong> Define ownership, management, and operating procedures</li>
              <li><strong>Obtain an EIN:</strong> Get your Employer Identification Number from the IRS for tax purposes</li>
            </ol>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Common LLC Mistakes to Avoid</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Many new business owners make these common mistakes when forming an LLC:
            </p>
            <ul className="list-disc pl-6 mb-8 space-y-3 text-gray-700">
              <li>Not separating personal and business finances</li>
              <li>Failing to maintain proper business records</li>
              <li>Missing annual report filing deadlines</li>
              <li>Operating without an operating agreement</li>
              <li>Not understanding state-specific requirements</li>
            </ul>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-12">
              <p className="text-gray-800 font-semibold mb-2">Ready to form your LLC?</p>
              <p className="text-gray-700 mb-4">Our expert team can guide you through every step of the LLC formation process.</p>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setIsModalOpen(true)}
              >
                Get Started Today
              </Button>
            </div>
          </div>
        </div>
      </article>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BlogPost1;